import React, { useEffect } from "react"

import moment from "moment-timezone"
import { useParams } from "react-router-dom"

import { IFetchedPoliciesExport } from "@common-shared/types/policies.types.ts"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook.ts"

import { GENERAL_CONFIG } from "../../config/general.config"
import { POLICIES_CONFIG } from "../../config/policies.config"
import { UtilHelper } from "../../helpers/util.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { policiesExportGetGenerated } from "../../store/thunks/policiesExport.thunks"
import { NotFoundComponent } from "../general/notFound.component"
import { PoliciesFiltersDisplayComponent } from "./filters/policiesFiltersDisplay.component"

export const DownloadExportComponent: React.FunctionComponent = () => {
    useScrollFix()

    const { exportFileName } = useParams()

    const dispatch = useAppDispatch()
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [fetchedExport, setFetchedExport] =
        React.useState<IFetchedPoliciesExport | null>(null)

    useEffect(() => {
        if (!exportFileName || !selectedPracticeRole || fetchedExport) {
            return
        }

        dispatch(
            policiesExportGetGenerated({
                practice: selectedPracticeRole.practice,
                payload: {
                    exportFileName,
                },
            }),
        )
            .unwrap()
            .then(setFetchedExport)
    }, [selectedPracticeRole?.practice?.id])

    const onDownloadExport = () => {
        if (!fetchedExport?.content.length) {
            return
        }

        UtilHelper.downloadBase64File(
            fetchedExport.content,
            `${fetchedExport.reportType}-${
                POLICIES_CONFIG.exportPoliciesBaseName
            }-${moment().format(
                GENERAL_CONFIG.defaultMomentDateTimeFormat,
            )}.csv`,
            "text/csv",
        )
    }

    return exportFileName ? (
        <div className="pb-32 relative">
            <PageHelmetElement
                title="Download Export"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {!fetchedExport ? (
                <SpinnerElement containerClassName="mt-[32px] text-center" />
            ) : (
                <div className="relative px-[24px] py-[12px]">
                    <PrimaryText size={29} lineHeight={35} weight="semibold">
                        {
                            POLICIES_CONFIG.headerTitleMapping[
                                fetchedExport.reportType
                            ]
                        }{" "}
                        export is ready
                    </PrimaryText>

                    <PoliciesFiltersDisplayComponent
                        prefixLabel="Applied filters:"
                        filters={fetchedExport.filters}
                        hideRemoveFilterButtons
                    />

                    <div className="my-[16px] h-[1px] bg-brand-warmShadow" />

                    <div className="mt-[32px] text-center">
                        <ButtonElement
                            label="Download export"
                            type="primary"
                            htmlType="button"
                            size="large"
                            onClick={onDownloadExport}
                        />
                    </div>
                </div>
            )}
        </div>
    ) : (
        <NotFoundComponent />
    )
}
